/* ============================================
   CATALOG STORE — Templates + metadonnees transitions
   ============================================
   Deux fichiers persistes :
   - templates: les textes de commentaire
   - meta: desc, category par transition (from>to)
   ============================================ */

import { ZONE_IDS, EVENT_TYPE_IDS, getTransitionKey, getTransitionTemplateAlias } from "./catalog-data.js"

let templates = []
let eventTemplates = []
let transitionMeta = {}  // { "D1>B": { desc: "Relance courte", category: "relance" }, ... }
let dirty = false
let eventDirty = false
let currentLang = "fr"

// ── Chargement ──

export async function loadTemplates(lang = "fr") {
  currentLang = lang
  try {
    const res = await fetch(`/api/templates/${lang}/flux`)
    if (res.ok) {
      const data = await res.json()
      templates = data.templates || []
    } else {
      templates = []
    }
  } catch {
    templates = []
  }
  dirty = false
  return templates
}

export async function loadTransitionMeta() {
  try {
    const res = await fetch(`/api/templates/_shared/meta`)
    if (res.ok) {
      const data = await res.json()
      transitionMeta = data.transitions || {}
    } else {
      transitionMeta = {}
    }
  } catch {
    transitionMeta = {}
  }
  return transitionMeta
}

export async function loadEventTemplates(lang = "fr") {
  currentLang = lang
  try {
    const res = await fetch(`/api/templates/${lang}/events`)
    if (res.ok) {
      const data = await res.json()
      eventTemplates = data.templates || []
    } else {
      eventTemplates = []
    }
  } catch {
    eventTemplates = []
  }
  eventDirty = false
  return eventTemplates
}

// ── Sauvegarde ──

export async function saveTemplates() {
  const payload = {
    version: 1,
    lang: currentLang,
    category: "flux",
    templates,
  }
  const metaPayload = {
    version: 1,
    transitions: transitionMeta,
  }
  try {
    const [res1, res2] = await Promise.all([
      fetch(`/api/templates/${currentLang}/flux`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }),
      fetch(`/api/templates/_shared/meta`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(metaPayload),
      }),
    ])
    if (res1.ok && res2.ok) {
      dirty = false
      return true
    }
  } catch { /* ignore */ }
  return false
}

export async function saveEventTemplates() {
  const payload = {
    version: 1,
    lang: currentLang,
    category: "events",
    templates: eventTemplates,
  }
  try {
    const res = await fetch(`/api/templates/${currentLang}/events`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
    if (res.ok) {
      eventDirty = false
      return true
    }
  } catch { /* ignore */ }
  return false
}

// ── Transition meta CRUD ──

export function getTransitionMeta(from, to) {
  const key = getTransitionKey(from, to)
  let meta = transitionMeta[key]
  if (meta && Object.keys(meta).length) return meta
  const alias = getTransitionTemplateAlias(from, to)
  if (alias) {
    const aliasKey = getTransitionKey(alias.from, alias.to)
    return transitionMeta[aliasKey] || null
  }
  return null
}

export function setTransitionMeta(from, to, meta) {
  const key = getTransitionKey(from, to)
  transitionMeta[key] = { ...(transitionMeta[key] || {}), ...meta }
  dirty = true
}

// ── Template CRUD ──

function templatesMatchingTransition(from, to) {
  return templates.filter((t) => {
    const fromMatch = t.fromZones.includes(from) || t.fromZones.includes("*")
    if (to === null) {
      // Premiere touche : templates avec toZones = ["_FT"]
      return fromMatch && t.toZones.includes("_FT")
    }
    const toMatch = t.toZones.includes(to) || t.toZones.includes("*")
    return fromMatch && toMatch
  })
}

export function getTemplatesForTransition(from, to) {
  const seen = new Set()
  const out = []
  function pushFrom(f, t) {
    for (const tpl of templatesMatchingTransition(f, t)) {
      if (!seen.has(tpl.id)) {
        seen.add(tpl.id)
        out.push(tpl)
      }
    }
  }
  pushFrom(from, to)
  const alias = getTransitionTemplateAlias(from, to)
  if (alias) pushFrom(alias.from, alias.to)
  return out
}

export function addTemplate(template) {
  templates.push(template)
  dirty = true
}

export function updateTemplate(id, changes) {
  const idx = templates.findIndex((t) => t.id === id)
  if (idx !== -1) {
    templates[idx] = { ...templates[idx], ...changes }
    dirty = true
  }
}

export function deleteTemplate(id) {
  const idx = templates.findIndex((t) => t.id === id)
  if (idx !== -1) {
    templates.splice(idx, 1)
    dirty = true
  }
}

export function generateTemplateId(from, to) {
  const ts = Date.now().toString(36)
  return `tpl_${from}_${to}_${ts}`
}

// ── Couverture ──

export function getTemplateCount(from, to) {
  return getTemplatesForTransition(from, to).length
}

const BASE_TEXTS = new Set(["{player}", "{prevPlayer} pour {player}"])

/**
 * Niveau de couverture d'une transition :
 *   "none"   — aucun template (rouge)
 *   "base"   — uniquement les templates de base (jaune)
 *   "ai"     — templates generes par IA, pas de modif humaine (bleu)
 *   "custom" — au moins un template personnalise humain (vert)
 */
export function getCoverageLevel(from, to) {
  const tpls = getTemplatesForTransition(from, to)
  if (tpls.length === 0) return "none"
  const customTpls = tpls.filter((t) => !BASE_TEXTS.has(t.text))
  if (customTpls.length === 0) return "base"
  const allAi = customTpls.every((t) => t.tags && t.tags.includes("ai_generated"))
  return allAi ? "ai" : "custom"
}

export function getCoverageStats() {
  let covered = 0
  // 18 zones x 18 destinations + 18 premieres touches = 342
  const total = ZONE_IDS.length * ZONE_IDS.length + ZONE_IDS.length
  for (const from of ZONE_IDS) {
    if (getTemplateCount(from, null) > 0) covered++  // premiere touche
    for (const to of ZONE_IDS) {
      if (getTemplateCount(from, to) > 0) covered++
    }
  }
  return { covered, total, percentage: total > 0 ? Math.round((covered / total) * 100) : 0 }
}

export function getZoneCoverageStats(fromZone) {
  let covered = 0
  const total = ZONE_IDS.length + 1  // destinations + premiere touche
  if (getTemplateCount(fromZone, null) > 0) covered++
  for (const to of ZONE_IDS) {
    if (getTemplateCount(fromZone, to) > 0) covered++
  }
  return { covered, total }
}

// ── Seed base templates ──

export function seedBaseTemplates() {
  let added = 0
  for (const from of ZONE_IDS) {
    // Premiere touche dans chaque zone (to = null → toZones = ["_FT"])
    const ftExisting = getTemplatesForTransition(from, null)
    if (!ftExisting.some((t) => t.text === "{player}")) {
      templates.push({
        id: `tpl_${from}_FT_ft`,
        text: "{player}",
        action: "PASS",
        fromZones: [from],
        toZones: ["_FT"],
        flank: null,
        phasePos: [],
        composedZone: null,
        weight: 1,
        cooldown: 3,
        tags: ["first_touch"],
      })
      added++
    }

    // Passe basique pour chaque transition (from → to)
    for (const to of ZONE_IDS) {
      const existing = getTemplatesForTransition(from, to)
      if (!existing.some((t) => t.text === "{prevPlayer} pour {player}")) {
        templates.push({
          id: `tpl_${from}_${to}_bp`,
          text: "{prevPlayer} pour {player}",
          action: "PASS",
          fromZones: [from],
          toZones: [to],
          flank: null,
          phasePos: [],
          composedZone: null,
          weight: 1,
          cooldown: 3,
          tags: [],
        })
        added++
      }
    }
  }
  if (added > 0) dirty = true
  return added
}

// ── Event template CRUD ──

export function getTemplatesForEvent(eventType) {
  return eventTemplates.filter((t) => t.eventType === eventType)
}

export function getEventTemplateCount(eventType) {
  return getTemplatesForEvent(eventType).length
}

export function addEventTemplate(template) {
  eventTemplates.push(template)
  eventDirty = true
}

export function updateEventTemplate(id, changes) {
  const idx = eventTemplates.findIndex((t) => t.id === id)
  if (idx !== -1) {
    eventTemplates[idx] = { ...eventTemplates[idx], ...changes }
    eventDirty = true
  }
}

export function deleteEventTemplate(id) {
  const idx = eventTemplates.findIndex((t) => t.id === id)
  if (idx !== -1) {
    eventTemplates.splice(idx, 1)
    eventDirty = true
  }
}

export function generateEventTemplateId(eventType) {
  const ts = Date.now().toString(36)
  const slug = eventType.replace(/[^a-zA-Z0-9]/g, "_").toLowerCase()
  return `evt_${slug}_${ts}`
}

export function getEventCoverageStats() {
  let covered = 0
  const total = EVENT_TYPE_IDS.length
  for (const evtId of EVENT_TYPE_IDS) {
    if (getEventTemplateCount(evtId) > 0) covered++
  }
  return { covered, total, percentage: total > 0 ? Math.round((covered / total) * 100) : 0 }
}

export function getAllEventTemplates() { return eventTemplates }

// ── Dirty state ──

export function isDirty() { return dirty || eventDirty }
export function isEventDirty() { return eventDirty }
export function markDirty() { dirty = true }
export function markEventDirty() { eventDirty = true }
export function getAllTemplates() { return templates }
